import { useState, useEffect, useCallback } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ShoppingCartOutlined } from '@mui/icons-material';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { SendingOrder } from 'src/modules/sending/models/sendingOrder';
import { formatCustomerSendDate } from 'src/modules/sending/functions/customerSendDate';
import { getAllSendingOrder, deleteSendingOrder } from 'src/shared/services/sendingService';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { SendingOrderStatusBadge } from 'src/modules/sending/components/sendingOrderStatusBadge';
import { SendingOrderStatusEnum } from 'src/modules/sending/models/sendingOrderStatus';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';

const SendingGrid = () => {
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<SendingOrder[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'created', sort: 'desc' }]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) updateData('loading', true);

    try {
      const result = await getAllSendingOrder(data.page, data.pageSize, sortModel[0]?.field, sortModel[0]?.sort);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        setRows(result.data.results);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) updateData('loading', false);
    }
  }, [data.page, data.pageSize, isMounted, sortModel]);

  useEffect(() => {
    if (isMounted()) fetchData();
  }, [fetchData, isMounted]);

  // Cell för ett utskick: statusbricka och utskicksdatum, eftersom varje utskick har sitt eget datum.
  const renderSendingCell = (status: number | undefined, date: string | Date | undefined, ordered = true) => {
    if (!ordered) {
      return (
        <Typography variant='body2' color='text.disabled'>
          Ej beställd
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <SendingOrderStatusBadge status={status ?? SendingOrderStatusEnum.Inskickad} />
        <Typography variant='body2' color='text.secondary' noWrap>
          {date ? formatCustomerSendDate(date) : ''}
        </Typography>
      </Box>
    );
  };

  const renderReminderStatus = (row: SendingOrder, which: 1 | 2) => {
    const ordered = which === 1 ? !!row.reminder1_date_id : !!row.reminder2_date_id;
    const status = which === 1 ? row.reminder1_status : row.reminder2_status;
    const date = which === 1 ? row.reminder1_date?.sndDate : row.reminder2_date?.sndDate;

    return renderSendingCell(status, date, ordered);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Nr',
      sortable: true,
      width: 80,
      // Beställningsnr – matchar numret i fakturamallens namn (t.ex. "Medlemsavisering 2026-10-20 (#81)").
      renderCell: (params: GridCellParams) => <>#{(params.row as SendingOrder).id}</>,
    },
    {
      field: 'product_id',
      headerName: 'Typ av utskick',
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => <>{(params.row as SendingOrder).product?.name ?? ''}</>,
    },
    {
      // Ett utskick per kolumn – rubriken säger vilket, cellen visar bara statusen. Tydligare än att trycka in
      // tre led på samma rad.
      field: 'status',
      headerName: 'Huvudutskick',
      sortable: true,
      flex: 1.4,
      renderCell: (params: GridCellParams) => {
        const row = params.row as SendingOrder;
        return renderSendingCell(row.status, row.date?.sndDate);
      },
    },
    {
      field: 'reminder1_status',
      headerName: 'Påminnelse 1',
      sortable: false,
      flex: 1.4,
      renderCell: (params: GridCellParams) => renderReminderStatus(params.row as SendingOrder, 1),
    },
    {
      field: 'reminder2_status',
      headerName: 'Påminnelse 2',
      sortable: false,
      flex: 1.4,
      renderCell: (params: GridCellParams) => renderReminderStatus(params.row as SendingOrder, 2),
    },
    {
      field: 'created',
      headerName: 'Skapad',
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const { created } = params.row as SendingOrder;
        return <>{created ? dayjs(created).format('YYYY-MM-DD HH:mm') : ''}</>;
      },
    },
    {
      field: 'createdByName',
      headerName: 'Skapad av',
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => <>{(params.row as SendingOrder).createdByName ?? ''}</>,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      width: 64,
      align: 'center',
      renderCell: (params: GridCellParams) => {
        const order = params.row as SendingOrder;
        return (
          <Tooltip title='Radera beställning'>
            <IconButton
              size='small'
              color='error'
              onClick={(e) => {
                e.stopPropagation();
                if (order.id) setDeleteId(order.id);
              }}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  // Radera-dialogen stängs antingen med bekräftelse ({ id }) eller avbryt (undefined).
  const handleDeleteClose = async (value?: { id: string }) => {
    const id = deleteId;
    setDeleteId(null);
    if (!value || id == null) return;
    try {
      await deleteSendingOrder(id);
      if (isMounted()) {
        toast.success('Beställningen har raderats.');
        fetchData();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log('error deleteSendingOrder', e);
        toast.error('Kunde inte radera beställningen.');
      }
    }
  };

  // Klick på en rad öppnar beställningen för att fortsätta arbeta med den (fas B).
  const onRowClick = (params) => {
    const order = params.row as SendingOrder;
    if (order.id) navigate(`${RoutesEnum.EditSendingOrder}/${order.id}`);
  };

  // Tom vy: inga beställningar och inget pågående anrop → snygg placeholder med uppmaning att skapa en beställning.
  const isEmpty = !data.loading && rows.length === 0;

  return (
    <Box sx={{ position: 'relative' }} ref={scrollRef}>
      {isEmpty ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: { xs: 6, sm: 10 },
            px: 3,
          }}
        >
          <ShoppingCartOutlined sx={{ fontSize: 64, color: 'primary.main', opacity: 0.35, mb: 2 }} />
          <Typography variant='h6' sx={{ fontWeight: 700, mb: 1 }}>
            Inga beställningar än
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 520, mb: 4 }}>
            Beställ fakturautskick för att få hjälp med att skapa och skicka ut medlemsfakturor till era medlemmar.
          </Typography>
          <Button
            variant='contained'
            color='primary'
            size='large'
            startIcon={<AddCircleIcon />}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            component={Link as any}
            to={RoutesEnum.CreateSendingOrder}
            disableElevation
          >
            Beställ nytt utskick
          </Button>
        </Box>
      ) : (
        <DataGrid<SendingOrder>
          columns={columns}
          rows={rows}
          autoHeight
          dataModel={data}
          checkboxSelection={false}
          onRowClick={onRowClick}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            if (newSortModel.length === 0) {
              const currentSort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
              setSortModel([{ field: sortModel[0].field, sort: currentSort }]);
            } else {
              setSortModel(newSortModel);
            }
          }}
          mode='server'
          selectionModel={[]}
          onSelectionModelChange={() => ConsoleHelper.log('')}
        />
      )}

      <DeleteConfirmationDialog
        open={deleteId !== null}
        value={deleteId != null ? String(deleteId) : ''}
        title='Radera beställning'
        modaltext='Är du säker på att du vill radera beställningen? Detta går inte att ångra.'
        confirmButtonText='Radera'
        confirmButtonColor='error'
        onClose={handleDeleteClose}
      />
    </Box>
  );
};

export default SendingGrid;
